import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getExpandedRowModel } from '@tanstack/react-table';
import { Icon } from '@iconify/react';

import { accentInsensitiveFilter } from '@core/utils';
import { Offcanvas } from '../Offcanvas';
import { ResponsiveDataListTable } from './ResponsiveDataListTable';

/**
 * Responsive Data List Component
 *
 * A generic, reusable component that displays data as:
 * - Table on desktop (using TanStack Table)
 * - Custom cards on mobile (via renderMobileItem prop)
 *
 * This component is 100% agnostic to your data structure.
 * Features like columns, actions, and mobile card design are
 * defined by the consumer.
 *
 * @param {Object} props
 * @param {Array} props.data - Array of data items
 * @param {Array} props.columns - TanStack Table column definitions
 * @param {Function} props.renderMobileItem - Render function for mobile cards
 * @param {string} [props.breakpoint='md'] - Breakpoint to switch views (sm/md/lg/xl)
 * @param {string} [props.emptyMessage='No items'] - Message when data is empty
 * @param {boolean} [props.enableSorting=false] - Enable column sorting
 * @param {boolean} [props.serverSideSorting=false] - Use server-side sorting (disables client-side sorting model)
 * @param {Array} [props.sorting] - Controlled sorting state for server-side sorting
 * @param {Function} [props.onSortingChange] - Sorting change handler for server-side sorting
 * @param {boolean} [props.enableFiltering=false] - Enable search/filtering
 * @param {boolean} [props.serverSideFiltering=false] - Use server-side filtering (disables client-side filtering model)
 * @param {string} [props.globalFilter] - Controlled global filter state for server-side filtering
 * @param {Function} [props.onGlobalFilterChange] - Global filter change handler for server-side filtering
 * @param {string} [props.searchPlaceholder='Search...'] - Search input placeholder
 * @param {boolean} [props.enableExpanding=false] - Enable expandable rows
 * @param {Function} [props.renderExpandedContent] - Render function for expanded row content
 * @param {boolean} [props.showFilters=false] - Show filters button next to search
 * @param {React.ReactNode} [props.filterContent] - Content to show in filters offcanvas
 * @param {number} [props.activeFiltersCount=0] - Number of active filters (shows badge)
 * @param {Object} [props...tableOptions] - Additional TanStack Table options
 *
 * @example
 * // Client-side sorting (default)
 * <ResponsiveDataList
 *   data={users}
 *   columns={columns}
 *   renderMobileItem={(row) => <UserCard user={row.original} />}
 *   enableSorting={true}
 * />
 *
 * @example
 * // Server-side sorting
 * const [sorting, setSorting] = useState([]);
 * <ResponsiveDataList
 *   data={users}
 *   columns={columns}
 *   renderMobileItem={(row) => <UserCard user={row.original} />}
 *   enableSorting={true}
 *   serverSideSorting={true}
 *   sorting={sorting}
 *   onSortingChange={setSorting}
 * />
 */
export function ResponsiveDataList({
  data,
  columns,
  renderMobileItem,
  breakpoint = 'md',
  emptyMessage = 'No items',
  enableSorting = false,
  serverSideSorting = false,
  sorting: controlledSorting,
  onSortingChange: controlledOnSortingChange,
  enableFiltering = false,
  serverSideFiltering = false,
  globalFilter: controlledGlobalFilter,
  onGlobalFilterChange: controlledOnGlobalFilterChange,
  searchPlaceholder,
  enableExpanding = false,
  renderExpandedContent,
  onRowClick,
  getRowProps,
  showFilters = false,
  filterContent,
  activeFiltersCount = 0,
  toolbarActions,
  ...tableOptions
}) {
  const { t } = useTranslation();

  // State for sorting and filtering
  // Use controlled state for server-side, otherwise internal state
  const [internalSorting, setInternalSorting] = useState(tableOptions.initialState?.sorting || []);
  const [internalGlobalFilter, setInternalGlobalFilter] = useState('');
  const [expanded, setExpanded] = useState({});
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  // Choose between controlled and internal state
  const sorting = serverSideSorting ? controlledSorting : internalSorting;
  const setSorting = serverSideSorting ? controlledOnSortingChange : setInternalSorting;

  const globalFilter = serverSideFiltering ? controlledGlobalFilter : internalGlobalFilter;
  const setGlobalFilter = serverSideFiltering ? controlledOnGlobalFilterChange : setInternalGlobalFilter;

  // Initialize TanStack Table with proper configuration
  // Property order is critical for TanStack Table v8 initialization
  const table = useReactTable({
    data,
    columns,
    defaultColumn: {
      size: 0,  // For flexible columns without size defined
    },
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    // Row models must come before state
    // For server-side sorting/filtering, disable client-side row models but keep UI
    ...(enableSorting && !serverSideSorting && { getSortedRowModel: getSortedRowModel() }),
    ...(enableFiltering && !serverSideFiltering && { getFilteredRowModel: getFilteredRowModel() }),
    ...(enableExpanding && {
      getExpandedRowModel: getExpandedRowModel(),
      // Enable expanding for all rows
      getRowCanExpand: () => true,
    }),
    // State change handlers
    ...(enableSorting && { onSortingChange: setSorting }),
    ...(enableFiltering && {
      onGlobalFilterChange: setGlobalFilter,
      globalFilterFn: accentInsensitiveFilter,
    }),
    ...(enableExpanding && { onExpandedChange: setExpanded }),
    // Enable manual mode for server-side operations
    ...(serverSideSorting && { manualSorting: true }),
    ...(serverSideFiltering && { manualFiltering: true }),
    // State object must be defined AFTER row models and handlers
    state: {
      ...(enableSorting && { sorting }),
      ...(enableFiltering && { globalFilter }),
      ...(enableExpanding && { expanded }),
    },
    ...tableOptions,
  });

  // Breakpoint class mappings
  const mobileClass = {
    sm: 'sm:hidden',
    md: 'md:hidden',
    lg: 'lg:hidden',
    xl: 'xl:hidden',
  }[breakpoint];

  const desktopClass = {
    sm: 'hidden sm:block',
    md: 'hidden md:block',
    lg: 'hidden lg:block',
    xl: 'hidden xl:block',
  }[breakpoint];

  // Optimize empty state checks - compute once
  const rows = table.getRowModel().rows;
  const isEmpty = !data || data.length === 0 || rows.length === 0;
  const hasNoResults = rows.length === 0 && globalFilter;

  return (
    <>
      {/* Toolbar: search input + optional actions (e.g. a status filter) + filters button */}
      {(enableFiltering || toolbarActions) && (
        <div className="mb-4 flex items-center gap-3">
          {/* Search Input */}
          {enableFiltering && (
            <div className="relative flex-1">
              <Icon
                icon="lucide:search"
                className="absolute left-3 top-1/2 -translate-y-1/2 icon-muted size-5"
              />
              <input
                type="text"
                value={globalFilter ?? ''}
                onChange={(e) => setGlobalFilter(e.target.value)}
                placeholder={searchPlaceholder || t('common.searchPlaceholder', 'Search...')}
                className="w-full pl-10 pr-10 py-2.5 border border-border-strong rounded-lg focus:ring-2 focus:ring-brand focus:border-brand transition-colors"
              />
              {globalFilter && (
                <button
                  onClick={() => setGlobalFilter('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 icon-muted hover:text-subtle transition-colors cursor-pointer"
                  aria-label={t('common.clearSearch', 'Clear search')}
                >
                  <Icon icon="lucide:x" className="size-5" />
                </button>
              )}
            </div>
          )}

          {/* Consumer-provided toolbar actions, aligned with the search input */}
          {toolbarActions}

          {/* Filters Button */}
          {enableFiltering && showFilters && (
            <button
              onClick={() => setIsFiltersOpen(true)}
              className="relative flex items-center gap-2 px-4 py-2.5 bg-white border border-border-strong rounded-lg hover:bg-gray-50 focus:ring-2 focus:ring-brand focus:border-brand transition-colors cursor-pointer"
              aria-label={t('common.filters', 'Filters')}
            >
              <Icon icon="lucide:filter" className="size-5 icon-muted" />
              <span className="text-sm font-medium">{t('common.filters', 'Filters')}</span>
              {activeFiltersCount > 0 && (
                <span className="absolute -top-2 -right-2 flex items-center justify-center size-6 rounded-full bg-brand text-white text-xs font-bold">
                  {activeFiltersCount}
                </span>
              )}
            </button>
          )}
        </div>
      )}

      {/* Empty state or No results */}
      {isEmpty ? (
        <div className="text-center py-12">
          {hasNoResults ? (
            <>
              <Icon
                icon="lucide:search-x"
                className="mx-auto size-12 text-border-strong mb-2"
              />
              <p className="text-sm text-muted">
                {t('common.noResultsFor', { query: globalFilter })}
              </p>
              <button
                onClick={() => setGlobalFilter('')}
                className="mt-2 text-sm text-brand hover:text-brand-dark cursor-pointer"
              >
                {t('common.clearSearch', 'Clear search')}
              </button>
            </>
          ) : (
            <p className="text-sm text-muted">{emptyMessage}</p>
          )}
        </div>
      ) : (
        <>
          {/* Mobile View: Custom Cards */}
          <div className={`${mobileClass} space-y-3`}>
            {rows.map((row) => (
              <div key={row.id}>
                {renderMobileItem(row)}
              </div>
            ))}
          </div>

          {/* Desktop View: Table */}
          <div className={desktopClass}>
            <div className="overflow-x-auto">
              <ResponsiveDataListTable
                table={table}
                renderExpandedContent={renderExpandedContent}
                onRowClick={onRowClick}
                getRowProps={getRowProps}
              />
            </div>
          </div>
        </>
      )}

      {/* Filters Offcanvas */}
      {showFilters && (
        <Offcanvas
          isOpen={isFiltersOpen}
          onClose={() => setIsFiltersOpen(false)}
          title={t('common.filters', 'Filters')}
        >
          {filterContent}
        </Offcanvas>
      )}
    </>
  );
}

ResponsiveDataList.propTypes = {
  data: PropTypes.array.isRequired,
  columns: PropTypes.array.isRequired,
  renderMobileItem: PropTypes.func.isRequired,
  breakpoint: PropTypes.oneOf(['sm', 'md', 'lg', 'xl']),
  emptyMessage: PropTypes.string,
  enableSorting: PropTypes.bool,
  serverSideSorting: PropTypes.bool,
  sorting: PropTypes.array,
  onSortingChange: PropTypes.func,
  enableFiltering: PropTypes.bool,
  serverSideFiltering: PropTypes.bool,
  globalFilter: PropTypes.string,
  onGlobalFilterChange: PropTypes.func,
  searchPlaceholder: PropTypes.string,
  enableExpanding: PropTypes.bool,
  renderExpandedContent: PropTypes.func,
  onRowClick: PropTypes.func,
  getRowProps: PropTypes.func,
  showFilters: PropTypes.bool,
  filterContent: PropTypes.node,
  activeFiltersCount: PropTypes.number,
  toolbarActions: PropTypes.node,
};
